/**
 * COMM-PASS domain model.
 *
 * Ported verbatim from the original single-file prototype's constants so behavior
 * and wording are preserved. Reference data that staff can extend at runtime
 * (members, carriers, custom lines) lives in the database; these are the fixed
 * program constants and the built-in defaults.
 */
export const TIERS = {
    complete: {
        key: 'complete',
        name: 'Alliance Complete',
        band: 'Tier 1 · Full-Service Referral',
        tag: 'Hands-off',
        member: 40,
        aas: 60,
        who: 'No commercial experience or capacity. You refer the lead; we do the rest.',
        does: 'AAS quotes, binds, services & renews. Passive income, no workload.',
        heavy: true,
    },
    assist: {
        key: 'assist',
        name: 'Alliance Assist',
        band: 'Tier 2 · Transitional Partnership',
        tag: 'Training phase',
        member: 60,
        aas: 40,
        who: 'Want to learn and eventually take over.',
        does: 'AAS quotes/binds/services for 3 years with training; you take over in Year 4.',
        heavy: true,
    },
    launch: {
        key: 'launch',
        name: 'Alliance Launch',
        band: 'Tier 3 · Quote & Bind Support',
        tag: 'Semi-hands-on',
        member: 70,
        aas: 30,
        who: 'Some experience, need quoting support.',
        does: 'AAS helps quote & bind; you service & renew from day one.',
        heavy: true,
    },
    gatekeeper: {
        key: 'gatekeeper',
        name: 'Gatekeeper Submission',
        band: 'Required Carrier · Handled by AAS',
        tag: 'Complete submission required',
        member: 0,
        aas: 0,
        gatekeeper: true,
        heavy: true,
        who: "You're placing a Gatekeeper-carrier risk we're required to submit on your behalf.",
        does: "Send us the full package — ACORDs, supplementals, loss runs, dec pages — plus a description " +
            "of operations and executive summary. We won't act until the submission is complete.",
    },
    // Not a Gatekeeper — a separate direct-appointment track. Same complete-package
    // requirements, but handled as its own tier/lane so Liberty stays distinct.
    liberty: {
        key: 'liberty',
        name: 'Liberty Mutual',
        band: 'Direct Appointment Track',
        tag: 'Appointment goal only',
        member: 0,
        aas: 0,
        gatekeeper: true,
        heavy: true,
        who: "You're pursuing a direct Liberty Mutual appointment — not a one-off placement.",
        does: 'AAS submits qualifying, in-appetite accounts to Liberty on your behalf. Consistent ' +
            'submissions bound in a relatively short time can lead to a direct appointment.',
    },
};
// COMM-PASS commission book: the producer's take is a FIXED 30% of the total
// commission on a bound deal, regardless of tier (per the user). Used by the
// auto-pull tracker on the /solutions page.
export const COMMPASS_PAYOUT_PCT = 30;
// Why a non-bound submission is being completed. Required when completing anything
// that isn't already 'bound' — shared by the queue board and the submission page so
// the two stay identical.
export const COMPLETE_REASONS = [
    'Quoted — agent placed elsewhere',
    'Declined by carrier(s)',
    'Agent unresponsive / abandoned',
    'Not a genuine quote request',
    'Duplicate submission',
    'Other',
];
// ---- File types ----
// MIME → file extension, for naming stored objects and email attachments so the
// recipient's OS/Excel/Word recognizes the file. (Excel rejects a spreadsheet
// whose name lacks a valid extension — e.g. "report_xlsx" instead of "report.xlsx".)
export const MIME_EXT = {
    'application/pdf': 'pdf',
    'image/png': 'png',
    'image/jpeg': 'jpg',
    'image/webp': 'webp',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'text/csv': 'csv',
    'text/plain': 'txt',
    'application/rtf': 'rtf',
    'text/rtf': 'rtf',
};
/**
 * Tidy a filename and ensure it ends with the right extension for its content type.
 * Also removes stray whitespace right before the extension (e.g. "Report .xlsx"),
 * which some mail/OS paths collapse into an underscore ("Report_xlsx") — leaving the
 * file with no valid extension so Excel/Word refuse to open it.
 */
export function ensureFileExt(name, mime) {
    let clean = (name || '').trim().replace(/\s+/g, ' ').replace(/\s+\.(?=[A-Za-z0-9]+$)/, '.');
    if (!clean)
        clean = 'document';
    const ext = mime ? MIME_EXT[mime] : undefined;
    if (!ext)
        return clean;
    return new RegExp(`\\.${ext}$`, 'i').test(clean) ? clean : `${clean}.${ext}`;
}
export const LOBS = [
    { id: 'gl', name: 'General Liability' },
    { id: 'prop', name: 'Commercial Property' },
    { id: 'bop', name: 'Business Owners (BOP)' },
    { id: 'auto', name: 'Commercial Auto' },
    { id: 'wc', name: 'Workers Comp' },
    { id: 'umb', name: 'Umbrella / Excess' },
    { id: 'eo', name: 'Professional / E&O' },
    { id: 'cyber', name: 'Cyber' },
    { id: 'inland', name: 'Inland Marine' },
];
export const LOB_NAME = Object.fromEntries(LOBS.map((l) => [l.id, l.name]));
export const FLOW = ['received', 'review', 'quoting', 'proposal_sent', 'bound'];
export const STATUS = {
    invited: { label: 'Awaiting Agent' },
    received: { label: 'Received' },
    review: { label: 'In Review' },
    quoting: { label: 'Quoting' },
    mga_creating: { label: 'Creating MGA Submission' },
    mga_sent: { label: 'Submission Sent to MGA' },
    info_needed: { label: 'Info Needed' },
    proposal_sent: { label: 'Proposal Sent' },
    bound: { label: 'Bound' },
    declined: { label: 'Declined' },
    closed_lost: { label: 'Closed — Not Bound' },
};
// The agent sees friendlier stage wording than the internal pipeline uses.
// (Staff keep the internal "…MGA" labels; agents see the generalized wording,
// since a submission can go to an MGA or a standard carrier's underwriter.)
export const AGENT_STATUS_LABEL = {
    proposal_sent: 'Proposal Ready',
    mga_creating: 'Preparing Submission',
    mga_sent: 'Submitted Directly',
};
export function statusLabelFor(status, reviewer) {
    if (!reviewer && AGENT_STATUS_LABEL[status])
        return AGENT_STATUS_LABEL[status];
    return STATUS[status]?.label ?? status;
}
export function flowIndex(s) {
    if (s === 'info_needed')
        return 1;
    if (s === 'declined' || s === 'closed_lost')
        return 1;
    if (s === 'invited')
        return -1;
    if (s === 'mga_creating' || s === 'mga_sent')
        return 2;
    return FLOW.indexOf(s);
}
export function isOffpath(s) {
    return s === 'info_needed' || s === 'declined' || s === 'closed_lost' || s === 'invited';
}
export const DOC_KINDS = [
    { v: 'acord', label: 'ACORD Form' },
    { v: 'decpage', label: 'Declaration Page' },
    { v: 'supplemental', label: 'Supplemental App' },
    { v: 'lossruns', label: 'Loss Runs' },
    { v: 'financials', label: 'Financials' },
    { v: 'discovery', label: 'Discovery' },
    { v: 'priorpol', label: 'Prior Policies' },
    { v: 'other', label: 'Other' },
];
export const DOC_KIND_LABEL = Object.fromEntries(DOC_KINDS.map((k) => [k.v, k.label]));
export const BILL_PLANS = [
    { id: 'full', label: 'Pay in full', desc: 'Single payment for the full annual premium.' },
    { id: '2pay', label: '2-pay (50% / 50%)', desc: 'Half down, balance in 90 days.' },
    {
        id: '4pay',
        label: 'Quarterly (4 installments)',
        desc: '25% down, then three quarterly payments.',
    },
    {
        id: 'monthly',
        label: 'Monthly (premium finance)',
        desc: 'Low down payment, monthly installments via a finance agreement.',
    },
];
export function billPlanLabel(id) {
    return BILL_PLANS.find((p) => p.id === id)?.label ?? '';
}
// ---- AAS contact block printed on customer-facing proposals ----
export const AAS_CONTACT = {
    name: 'Agents Alliance Services, Ltd.',
    addr: 'Denton, TX',
    phone: '(940) 435-1119',
    email: 'commpass@agentsalliance.biz',
    web: 'agentsalliance.com',
};
// Blank Discovery form template, served from the app's own public assets (no login).
// Used by the wizard's "Get the blank Discovery form" link, and surfaced to agents
// whenever staff request the "Discovery" document type.
export const DISCOVERY_FORM_PATH = '/Discovery-Commercial-Lines-New-Business.xlsx';
export const DISCOVERY_DOC_LABEL = 'Discovery';
// ---- Document types staff can request from an agent ----
export const DOC_REQUEST_TYPES = [
    'ACORD Form',
    'Declaration Page',
    'Supplemental App',
    'Loss Runs',
    'Financials',
    'Discovery',
    'Prior Policies',
    'Other',
];
export const CARRIER_LIST = [
    { name: 'Travelers', link: 'https://signin.travelers.com/' },
    { name: 'SECURA', link: 'https://www.secura.net/agents' },
    { name: 'Acuity', link: 'https://www.acuity.com/portal' },
    { name: 'Liberty Mutual', link: 'https://commercial-agent.libertymutual.com/' },
    { name: 'The Hartford', link: 'https://www.thehartford.com/business-insurance/agents' },
    { name: 'Nationwide', link: 'https://www.nationwide.com/business/agents' },
    { name: 'Progressive Commercial', link: 'https://www.forprogressiveagents.com' },
    { name: 'Chubb', link: 'https://www.chubb.com/us-en/business-insurance/' },
];
export const CARRIER_STATUS_LABEL = {
    targeted: 'Approaching',
    submitted: 'Submitted to carrier',
    quoted: 'Quoted',
    declined: 'Declined',
};
// Per-submission progress for a selected Gatekeeper carrier.
export const GK_STATUS_LABEL = {
    pending: 'Selected — not yet sent',
    submitted: 'Submitted',
    quoted: 'Quoted',
    declined: 'Declined',
};
export const GATEKEEPER_CARRIER_OPTIONS = [
    { key: 'bitco', name: 'BITCO', appetiteDoc: '/appetite/bitco.pdf' },
    { key: 'emc', name: 'EMC', appetiteDoc: '/appetite/emc.pdf' },
    {
        key: 'fcci',
        name: 'FCCI',
        appetiteDoc: '/appetite/fcci.pdf',
        appetiteNote: 'Some pages show an older "as of" date (e.g. Q3/Q4 2024) — the appetite is still current and accurate.',
    },
    { key: 'liberty', name: 'Liberty Mutual', needsAppointmentAck: true },
];
// Reminder shown above the Gatekeeper carrier appetite checks.
export const APPETITE_REMINDER = "Before you continue, open each selected carrier's appetite guide and confirm this risk fits their appetite — " +
    'it saves everyone a bounced submission.';
// Shown when an agent selects Liberty Mutual as a Gatekeeper carrier — they must confirm.
export const LIBERTY_APPOINTMENT_NOTE = 'Liberty Mutual — only if a direct appointment is your goal. Liberty is not for one-offs; ' +
    'they want to see consistent submission of accounts in their appetite before appointing. ' +
    "Please don't send Liberty business unless earning an appointment is the goal. Have a one-off? " +
    'Send it to RPS instead.';
export const SOL_STAGES = [
    { k: 'new', label: 'New' },
    { k: 'added_ams', label: 'Added to AMS' },
    { k: 'quoting', label: 'Quoting' },
    { k: 'quoted', label: 'Quoted' },
    { k: 'bound', label: 'Bound' },
    { k: 'closed', label: 'Closed' },
];
export const SOL_STAGE_LABEL = Object.fromEntries(SOL_STAGES.map((s) => [s.k, s.label]));
export const SOL_ACCOUNT_TYPES = [
    'Commercial Lines',
    'Personal Lines',
    'Benefits',
    'Bonds',
    'Other',
];
export const SOL_CHECKLIST = [
    ['lossruns', 'Loss runs received'],
    ['proposal_sent', 'Proposal sent to client'],
    ['signed_app', 'Signed application returned'],
    ['down_payment', 'Down payment collected'],
    ['cois', 'COIs / ID cards issued'],
];
// ---- Fields the AI extracts from uploaded documents ----
export const EXTRACT_FIELDS = [
    ['namedInsured', 'Named insured'],
    ['dba', 'DBA / trade name'],
    ['mailingAddress', 'Mailing address'],
    ['physicalAddress', 'Physical address'],
    ['fein', 'FEIN'],
    ['entityType', 'Entity type'],
    ['yearsInBusiness', 'Years in business'],
    ['contactName', 'Contact name'],
    ['contactPhone', 'Contact phone'],
    ['contactEmail', 'Contact email'],
    ['naics', 'SIC / NAICS'],
    ['annualRevenue', 'Annual revenue'],
    ['annualPayroll', 'Annual payroll'],
    ['employees', '# Employees'],
    ['effectiveDate', 'Effective date'],
    ['expirationDate', 'Expiration date'],
    ['currentCarrier', 'Current carrier'],
    ['premium', 'Premium'],
    ['policyNumber', 'Policy number'],
    ['lossSummary', 'Loss summary'],
];
